/**
 * Monthly Cash Flow tab — redesigned line-item breakdown.
 *
 * Layout matches the find-properties.html redesign:
 *   • Income — pill header (green) → Gross rent (display) + Other income
 *     (editable) + Effective gross income subtotal.
 *   • Operating Expenses — pill header (red) → 8 editable lines, each with a
 *     two-line label (name + contextual sub) and a +/− sign chip, then a
 *     Total operating expenses subtotal with a "% of rent" hint.
 *   • Net cash flow by financing — comparison cards that vary by strategy:
 *       BRRRR  → Short-Term (Hard money) + Long-Term (After refi)
 *       Flip   → Short-Term (Hard money) only
 *       Rental → Long-Term (Conventional loan) only
 *
 * Edits to expense rows flow into the parent's `cashflowOverrides`, which
 * `deriveMetrics` reads to compute NOI / DSCR.
 */
import { ChartBar, Clock, TrendingDown, TrendingUp } from "lucide-react";

import { NumberInput } from "@/components/ui/number-input";
import { cn } from "@/lib/utils";
import { computeBrrrrRefi, fmtMoney, termInMonths } from "@/lib/calculators";
import type { CashflowOverrides, LoanInputs, Strategy } from "@/types/pipeline";

import type { DealInputsState, DerivedMetrics } from "../detail-helpers";

interface MonthlyCashflowProps {
  state: DealInputsState;
  metrics: DerivedMetrics;
  /** Active strategy — drives which financing comparison cards are shown. */
  strategy: Strategy;
  onCashflowChange: (patch: CashflowOverrides) => void;
}

/** Round to at most 2 decimal places (avoids float noise like 11.074456…). */
const round2 = (n: number) => Math.round(n * 100) / 100;

type ExpenseField = keyof Pick<
  CashflowOverrides,
  "vacancy" | "mgmt" | "taxes" | "insurance" | "maint" | "capex" | "hoa" | "utilities"
>;

interface ExpenseRowDef {
  id: ExpenseField;
  label: string;
  /** Sub-label formatter; receives the current value + computed context. */
  sub?: (value: number, ctx: { rent: number; purchase: number }) => string | undefined;
  /** Static sub-label when no dynamic hint applies. */
  staticSub?: string;
}

const EXPENSE_ROWS: ExpenseRowDef[] = [
  {
    id: "vacancy",
    label: "Vacancy reserve",
    sub: (v, { rent }) => (rent > 0 ? `${((v / rent) * 100).toFixed(1)}% of rent` : undefined),
  },
  {
    id: "mgmt",
    label: "Property management",
    sub: (v, { rent }) => (rent > 0 ? `${((v / rent) * 100).toFixed(1)}% of rent` : undefined),
  },
  {
    id: "taxes",
    label: "Property taxes",
    sub: (v, { purchase }) =>
      purchase > 0 ? `${(((v * 12) / purchase) * 100).toFixed(2)}% of price / yr` : undefined,
  },
  { id: "insurance", label: "Insurance", staticSub: "Landlord policy" },
  {
    id: "maint",
    label: "Maintenance reserve",
    sub: (v, { rent }) => (rent > 0 ? `${((v / rent) * 100).toFixed(1)}% of rent` : undefined),
  },
  {
    id: "capex",
    label: "CapEx reserve",
    sub: (v, { rent }) => (rent > 0 ? `${((v / rent) * 100).toFixed(1)}% of rent` : undefined),
  },
  { id: "hoa", label: "HOA fees", staticSub: "If applicable" },
  { id: "utilities", label: "Utilities", staticSub: "Owner-paid only" },
];

export function MonthlyCashflow({
  state,
  metrics,
  strategy,
  onCashflowChange,
}: MonthlyCashflowProps) {
  const overrides = state.cashflowOverrides ?? {};
  const cf = metrics.cashflow;
  const grossRent = overrides.gross ?? state.rent;

  // Defaults pulled from deriveMetrics for display when no override exists.
  const defaults: Record<ExpenseField, number> = {
    vacancy: 0,
    mgmt: 0,
    taxes: state.purchase * 0.00103,
    insurance: state.purchase * 0.0005,
    maint: state.rent * 0.08,
    capex: state.rent * 0.06,
    hoa: 0,
    utilities: 0,
  };
  const opexPctOfRent =
    grossRent > 0 ? `${((cf.opex / grossRent) * 100).toFixed(1)}% of rent` : undefined;

  // ── Financing comparison cards ──
  // Short-term (hard money / flip) debt is the active loan from metrics.
  const shortDebt = metrics.monthlyDebt;
  // Long-term refi payment (BRRRR) — computed against ARV at the refi LTV.
  const longLoan =
    state.brrrr.long_term ?? ({ ltv: 75, rate: 7, term: 30, points: 1 } as LoanInputs);
  const refiLtv = longLoan.ltv ?? 75;
  const refiEmi = computeBrrrrRefi({
    arv: state.arv,
    refiLtvPct: refiLtv,
    refiRatePct: longLoan.rate,
    refiTermYears: termInMonths(longLoan, "years") / 12,
    refiPoints: longLoan.points,
    hmlLoanAmt: metrics.loanAmount,
    hmlMonthlyInterest: shortDebt,
    holdMonths: state.brrrr.hold_months ?? 6,
    totalInDeal: state.purchase + state.rehab + metrics.closingDollar + metrics.origFee,
  }).refiEmi;

  const cardCtx = { noi: cf.noi, egi: cf.egi };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <ChartBar className="h-4 w-4 text-primary" aria-hidden />
        Monthly Cash Flow
      </h3>

      {/* ── Income ── */}
      <SectionPill tone="income">Income</SectionPill>
      <div>
        <CfRow name="Gross rent" sub="From monthly rent" displayValue={grossRent} tone="income" />
        <CfRow
          name="Other income"
          sub="Parking · laundry · pets"
          tone="income"
          editable={{
            value: overrides.other ?? 0,
            onChange: (v) => onCashflowChange({ other: v }),
          }}
        />
        <CfRow name="Effective gross income" displayValue={cf.egi} tone="income" subtotal />
      </div>

      {/* ── Operating Expenses ── */}
      <SectionPill tone="expense">Operating Expenses</SectionPill>
      <div>
        {EXPENSE_ROWS.map((row) => {
          const value = round2(overrides[row.id] ?? defaults[row.id]);
          const sub = row.sub
            ? row.sub(value, { rent: grossRent, purchase: state.purchase })
            : row.staticSub;
          return (
            <CfRow
              key={row.id}
              name={row.label}
              sub={sub}
              tone="expense"
              editable={{
                value,
                onChange: (v) => onCashflowChange({ [row.id]: round2(v) }),
              }}
            />
          );
        })}
        <CfRow
          name="Total operating expenses"
          sub={opexPctOfRent}
          displayValue={cf.opex}
          tone="expense"
          subtotal
        />
      </div>

      {/* ── Net cash flow by financing ── */}
      <div className="mt-2 border-t border-border pt-3.5">
        <div className="mb-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
          Net cash flow by financing
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(strategy === "BRRRR" || strategy === "Flip") && (
            <FinancingCard
              variant="short"
              label="Short-Term"
              debtLabel="Hard money"
              debt={shortDebt}
              {...cardCtx}
            />
          )}
          {strategy === "BRRRR" && (
            <FinancingCard
              variant="long"
              label="Long-Term"
              debtLabel="After refi"
              debt={refiEmi}
              {...cardCtx}
            />
          )}
          {strategy === "Rental" && (
            <FinancingCard
              variant="long"
              label="Long-Term"
              debtLabel="Conventional loan"
              debt={shortDebt}
              {...cardCtx}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Section pill — green "Income" / red "Operating Expenses" header.
 * ────────────────────────────────────────────────────────────────────────── */

function SectionPill({
  tone,
  children,
}: {
  tone: "income" | "expense";
  children: React.ReactNode;
}) {
  const isIncome = tone === "income";
  const Icon = isIncome ? TrendingUp : TrendingDown;
  return (
    <div
      className={cn(
        "mb-2.5 mt-4 inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5",
        "text-[10.5px] font-bold uppercase tracking-[0.08em] first:mt-1",
        isIncome
          ? "border-success/30 bg-success/10 text-success"
          : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Cash-flow row — two-line label + signed value (editable input or display).
 * ────────────────────────────────────────────────────────────────────────── */

interface CfRowProps {
  name: string;
  sub?: string;
  tone: "income" | "expense";
  /** Editable money input (always a positive value). */
  editable?: { value: number; onChange: (v: number) => void };
  /** Read-only display value (rendered with the tone's sign + color). */
  displayValue?: number;
  /** Emphasised subtotal row (top dashed border, larger value). */
  subtotal?: boolean;
}

function CfRow({ name, sub, tone, editable, displayValue, subtotal }: CfRowProps) {
  const isExpense = tone === "expense";
  const sign = isExpense ? "−" : "+";
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3.5 py-[5px]",
        subtotal
          ? "mt-1 border-t border-dashed border-border pt-3"
          : "border-b border-border last:border-b-0",
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div
          className={cn(
            "text-[13.5px] text-foreground",
            subtotal ? "font-bold" : "font-semibold",
          )}
        >
          {name}
        </div>
        {sub && (
          <div className="text-[11.5px] leading-tight text-muted-foreground">{sub}</div>
        )}
      </div>

      {editable ? (
        <div className="flex flex-shrink-0 items-center gap-2">
          <span
            className={cn(
              "w-3 text-center text-sm font-bold",
              isExpense ? "text-destructive" : "text-success",
            )}
          >
            {sign}
          </span>
          <div
            className={cn(
              "flex w-[200px] items-center rounded-md border bg-card focus-within:border-primary",
              isExpense ? "border-destructive/30" : "border-border",
            )}
          >
            <span className="pl-2 text-xs text-muted-foreground">$</span>
            <NumberInput
              value={editable.value}
              onChange={editable.onChange}
              className={cn(
                "h-7 border-0 bg-transparent pl-1 pr-2 text-right font-bold focus-visible:ring-0 focus-visible:ring-offset-0",
                isExpense && "text-destructive",
              )}
              aria-label={name}
            />
          </div>
          <span className="text-xs font-medium text-muted-foreground">/mo</span>
        </div>
      ) : (
        <span
          className={cn(
            "flex-shrink-0 font-bold tabular-nums",
            subtotal ? "text-[15px] font-extrabold" : "text-sm",
            isExpense ? "text-destructive" : "text-success",
          )}
        >
          {sign}
          {fmtMoney(Math.abs(displayValue ?? 0))}
        </span>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Financing comparison card — one per financing path (Short / Long term).
 * ────────────────────────────────────────────────────────────────────────── */

interface FinancingCardProps {
  variant: "short" | "long";
  /** Card title pill text. */
  label: string;
  /** Sub-label describing the loan source ("Hard money", "After refi", …). */
  debtLabel: string;
  /** Monthly debt service for this financing path. */
  debt: number;
  /** Monthly net operating income (shared across cards). */
  noi: number;
  /** Effective gross income (for the margin stat). */
  egi: number;
}

function FinancingCard({ variant, label, debtLabel, debt, noi, egi }: FinancingCardProps) {
  const net = noi - debt;
  const annual = net * 12;
  const margin = egi > 0 ? (net / egi) * 100 : 0;
  const dscr = debt > 0 ? noi / debt : 0;
  const isShort = variant === "short";
  const Icon = isShort ? Clock : TrendingUp;

  return (
    <div
      className={cn(
        "flex flex-col rounded-[10px] border bg-muted/40 p-4",
        isShort ? "border-warning/30" : "border-success/30",
      )}
    >
      <div className="mb-2.5 flex items-center justify-between gap-2.5">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1",
            "text-[10px] font-bold uppercase tracking-[0.08em]",
            isShort
              ? "border-warning/30 bg-warning/15 text-warning"
              : "border-success/30 bg-success/15 text-success",
          )}
        >
          <Icon className="h-3 w-3" aria-hidden />
          {label}
        </span>
        <span className="text-[11.5px] font-medium text-muted-foreground">{debtLabel}</span>
      </div>

      <div className="mb-2 flex flex-col gap-px">
        <div className="text-[11px] font-semibold text-muted-foreground">Debt service</div>
        <div className="text-sm font-bold tabular-nums text-destructive">
          −{fmtMoney(debt)}
          <span className="ml-0.5 text-[11.5px] font-medium text-muted-foreground">/mo</span>
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-px">
        <div className="text-[11px] font-semibold text-muted-foreground">Monthly cash flow</div>
        <div
          className={cn(
            "text-[22px] font-extrabold leading-tight tabular-nums",
            net >= 0 ? "text-success" : "text-destructive",
          )}
        >
          {net >= 0 ? "+" : "−"}
          {fmtMoney(Math.abs(net))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5 border-t border-dashed border-border pt-2.5">
        <StatRow
          k="Annual"
          v={`${annual >= 0 ? "+" : "−"}${fmtMoney(Math.abs(annual))}`}
          tone={annual >= 0 ? "pos" : "neg"}
        />
        <StatRow
          k="Margin"
          v={`${margin >= 0 ? "+" : ""}${margin.toFixed(1)}%`}
          tone={margin >= 0 ? "pos" : "neg"}
        />
        <StatRow
          k="DSCR"
          v={dscr.toFixed(2)}
          tone={dscr >= 1.2 ? "pos" : dscr >= 1 ? "neutral" : "neg"}
        />
      </div>
    </div>
  );
}

function StatRow({
  k,
  v,
  tone,
}: {
  k: string;
  v: string;
  tone: "pos" | "neg" | "neutral";
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span
        className={cn(
          "font-bold tabular-nums",
          tone === "pos" && "text-success",
          tone === "neg" && "text-destructive",
          tone === "neutral" && "text-foreground",
        )}
      >
        {v}
      </span>
    </div>
  );
}
